import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownIcon, ArrowLeftIcon, ArrowUpIcon, DownloadIcon, PlusIcon, TrashIcon } from "@primer/octicons-react";
import { ActionList, ActionMenu, FormControl, IconButton, Select, Text } from "@primer/react";
import { Alert } from "../components/Alert/Alert.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ClearableTextInput } from "../components/ClearableTextInput/ClearableTextInput.jsx";
import { formatPercentage } from "../components/EditablePercentage/EditablePercentage.jsx";
import { amountForBasisPoints, formatPortfolioAmount } from "../components/PortfolioValue/PortfolioValue.jsx";
import { formatPortfolioValueDraft } from "../components/PortfolioValue/PortfolioValueDialog.jsx";
import { PolicyRangeMeter } from "../components/PolicyRangeMeter/PolicyRangeMeter.jsx";
import { policyTitle } from "../components/PolicyIdentity/PolicyIdentity.jsx";
import { evaluateCompliance } from "../domain/compliance.js";
import { effectiveValue } from "../domain/document.js";
import { sortProfilesByName } from "../lib/sortProfilesByName.js";
import { usePresentationState } from "../storage/presentationState.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import reportDocumentStyles from "./ReportDocument.css?raw";
import policyRangeMeterStyles from "../components/PolicyRangeMeter/PolicyRangeMeter.css?raw";
import "./ReportDocument.css";
import "./ReportBuilderPage.css";

const defaultReportDraft = {
  targetProfileId: "",
  actualProfileId: "",
  portfolioValueInput: "",
  reportTitle: "Regulator Report",
  recipientName: "",
  sectionOrder: ["assets"],
  breakdownViewId: ""
};
const reportSections = [
  { id: "assets", label: "Assets" },
  { id: "breakdown", label: "Portfolio breakdown" },
  { id: "policies", label: "Policies" }
];
const reportDraftState = { lifetime: "persistent", name: "draft", page: "reports", scope: "application" };

export function ReportBuilderPage() {
  const application = useApplicationDocument();
  const [draft, setDraft] = usePresentationState({ ...reportDraftState, defaultValue: defaultReportDraft, validate: validReportDraft });
  const { actualProfileId, breakdownViewId, portfolioValueInput, recipientName, reportTitle, sectionOrder, targetProfileId } = draft;
  const valueInputRef = useRef(null);
  const reportRef = useRef(null);

  useEffect(() => {
    if (!application.profile || targetProfileId) return;
    setDraft((current) => current.targetProfileId ? current : { ...current, targetProfileId: application.profile.id });
  }, [application.profile, setDraft, targetProfileId]);

  if (application.error && !application.applicationDocument) {
    return <main className="report-builder-page"><Alert tone="danger" heading="Unable to build a report">{application.error.message}</Alert></main>;
  }
  if (!application.applicationDocument) return <ReportBuilderLoading />;

  const profiles = sortProfilesByName(application.profiles);
  const targetProfile = profiles.find((profile) => profile.id === targetProfileId) ?? application.profile;
  const compatibleProfiles = profiles.filter((profile) => profile.id !== targetProfile.id && profilesAreComparable(profile, targetProfile));
  const actualProfile = compatibleProfiles.find((profile) => profile.id === actualProfileId) ?? null;
  const portfolioValue = parsePortfolioValue(portfolioValueInput);
  const invalidValue = portfolioValueInput.trim() !== "" && portfolioValue === null;

  function selectTarget(profileId) {
    const nextTarget = profiles.find((profile) => profile.id === profileId);
    const selectedActual = profiles.find((profile) => profile.id === actualProfileId);
    const nextViews = nextTarget?.data.browserViews ?? [];
    updateDraft({
      targetProfileId: profileId,
      actualProfileId: nextTarget && selectedActual && !profilesAreComparable(selectedActual, nextTarget) ? "" : actualProfileId,
      breakdownViewId: nextViews.some((view) => view.id === breakdownViewId) ? breakdownViewId : (nextViews[0]?.id ?? "")
    });
  }

  function updateDraft(changes) {
    setDraft((current) => ({ ...current, ...changes }));
  }

  function addSection(sectionId) {
    updateDraft({
      sectionOrder: [...sectionOrder, sectionId],
      breakdownViewId: sectionId === "breakdown" && !breakdownViewId ? (targetProfile.data.browserViews?.[0]?.id ?? "") : breakdownViewId
    });
  }

  function moveSection(sectionId, offset) {
    const index = sectionOrder.indexOf(sectionId);
    const destination = index + offset;
    if (index < 0 || destination < 0 || destination >= sectionOrder.length) return;
    const nextOrder = [...sectionOrder];
    [nextOrder[index], nextOrder[destination]] = [nextOrder[destination], nextOrder[index]];
    updateDraft({ sectionOrder: nextOrder });
  }

  function deleteSection(sectionId) {
    updateDraft({ sectionOrder: sectionOrder.filter((id) => id !== sectionId) });
  }

  function changePortfolioValue(event) {
    const rawValue = event.target.value;
    const formattedValue = formatPortfolioValueDraft(rawValue);
    const nextInput = formattedValue ?? rawValue;
    const logicalOffset = rawValue.slice(0, event.target.selectionStart ?? rawValue.length).replaceAll(",", "").length;
    updateDraft({ portfolioValueInput: nextInput });
    if (formattedValue !== null) requestAnimationFrame(() => {
      const caret = caretForLogicalOffset(formattedValue, logicalOffset);
      valueInputRef.current?.setSelectionRange(caret, caret);
    });
  }

  function downloadReport() {
    if (!reportRef.current) return;
    const title = [reportTitle, recipientName].filter(Boolean).join(": ") || "Portfolio report";
    downloadHtmlFile(reportFilename(recipientName || targetProfile.name), standaloneReportHtml(title, reportMarkupForDownload(reportRef.current)));
  }

  return <main className="report-builder-page">
    <header className="report-builder-header">
      <Button as="a" href="#/profiles" tone="secondary" leadingVisual={ArrowLeftIcon}>Back to Profiles</Button>
      <div className="report-builder-heading"><h1>Report builder</h1><Text as="p">Choose the portfolio roles and see the report update below.</Text></div>
      <Button leadingVisual={DownloadIcon} onClick={downloadReport}>Download report</Button>
    </header>

    <section className="report-setup" aria-labelledby="report-setup-heading">
      <h2 id="report-setup-heading">Setup</h2>
      <div className="report-setup-fields">
        <FormControl>
          <FormControl.Label>Target profile</FormControl.Label>
          <Select block value={targetProfile.id} onChange={(event) => selectTarget(event.target.value)}>
            {profiles.map((profile) => <Select.Option value={profile.id} key={profile.id}>{profile.name}</Select.Option>)}
          </Select>
          <FormControl.Caption>The allocation the portfolio should reach.</FormControl.Caption>
        </FormControl>
        <FormControl>
          <FormControl.Label>Actual profile</FormControl.Label>
          <Select block value={actualProfile?.id ?? ""} onChange={(event) => updateDraft({ actualProfileId: event.target.value })}>
            <Select.Option value="">None</Select.Option>
            {compatibleProfiles.map((profile) => <Select.Option value={profile.id} key={profile.id}>{profile.name}</Select.Option>)}
          </Select>
          <FormControl.Caption>Optional comparison using the same asset list.</FormControl.Caption>
        </FormControl>
        <FormControl validationStatus={invalidValue ? "error" : undefined}>
          <FormControl.Label>Portfolio value</FormControl.Label>
          <ClearableTextInput ref={valueInputRef} block aria-label="Report portfolio value" clearLabel="Clear report portfolio value" inputMode="decimal" maxLength={64} value={portfolioValueInput} onChange={changePortfolioValue} onClear={() => updateDraft({ portfolioValueInput: "" })} />
          {invalidValue ? <FormControl.Validation variant="error">Enter a number greater than zero with up to two decimal places.</FormControl.Validation> : <FormControl.Caption>Used to calculate target amounts.</FormControl.Caption>}
        </FormControl>
      </div>
    </section>

    <div className="report-preview-stage"><AllocationReport actualProfile={actualProfile} breakdownViewId={breakdownViewId} onAddSection={addSection} onBreakdownViewChange={(viewId) => updateDraft({ breakdownViewId: viewId })} onDeleteSection={deleteSection} onMoveSection={moveSection} onRecipientNameChange={(value) => updateDraft({ recipientName: value })} onReportTitleChange={(value) => updateDraft({ reportTitle: value })} portfolioValue={portfolioValue} recipientName={recipientName} reportRef={reportRef} reportTitle={reportTitle} sectionOrder={sectionOrder} targetProfile={targetProfile} /></div>
  </main>;
}

function AddSectionMenu({ onAdd, sectionOrder, targetProfile }) {
  const availableSections = reportSections.filter((section) => !sectionOrder.includes(section.id));
  return <ActionMenu>
    <ActionMenu.Button disabled={!availableSections.length} leadingVisual={PlusIcon}>Add section</ActionMenu.Button>
    <ActionMenu.Overlay><ActionList>{availableSections.map((section) => <ActionList.Item disabled={section.id === "breakdown" && !targetProfile.data.browserViews?.length} key={section.id} onSelect={() => onAdd(section.id)}>{section.label}</ActionList.Item>)}</ActionList></ActionMenu.Overlay>
  </ActionMenu>;
}

function AllocationReport({ actualProfile, breakdownViewId, onAddSection, onBreakdownViewChange, onDeleteSection, onMoveSection, onRecipientNameChange, onReportTitleChange, portfolioValue, recipientName, reportRef, reportTitle, sectionOrder, targetProfile }) {
  const rows = useMemo(() => allocationRows(targetProfile, actualProfile), [actualProfile, targetProfile]);
  return <article className="report-document" aria-label="Report preview" ref={reportRef}>
    <header className="report-title">
      <h1><InlineReportText emptyLabel="Add report title" label="report title" maxLength={80} onChange={onReportTitleChange} value={reportTitle} /><span className={reportTitle && recipientName ? "" : "report-editor-only"}>: </span><strong><InlineReportText emptyLabel="Add recipient name" label="recipient name" maxLength={120} onChange={onRecipientNameChange} value={recipientName} /></strong></h1>
    </header>
    {sectionOrder.map((sectionId, index) => {
      const editorProps = { index, name: reportSections.find((section) => section.id === sectionId)?.label ?? sectionId, onDelete: () => onDeleteSection(sectionId), onMove: (offset) => onMoveSection(sectionId, offset), sectionCount: sectionOrder.length };
      if (sectionId === "assets") return <AssetsSection actualProfile={actualProfile} editorProps={editorProps} index={index} key={sectionId} portfolioValue={portfolioValue} rows={rows} />;
      if (sectionId === "breakdown") return <PortfolioBreakdownSection actualProfile={actualProfile} editorProps={editorProps} index={index} key={sectionId} onViewChange={onBreakdownViewChange} portfolioValue={portfolioValue} targetProfile={targetProfile} viewId={breakdownViewId} />;
      if (sectionId === "policies") return <PoliciesSection actualProfile={actualProfile} editorProps={editorProps} index={index} key={sectionId} targetProfile={targetProfile} />;
      return null;
    })}
    <div className="report-add-section report-editor-only"><AddSectionMenu onAdd={onAddSection} sectionOrder={sectionOrder} targetProfile={targetProfile} /></div>
    <ReportMetadata actualProfile={actualProfile} targetProfile={targetProfile} />
  </article>;
}

function SectionEditor({ children, index, name, onDelete, onMove, sectionCount }) {
  return <div className="report-section-editor report-editor-only">
    <strong>{name}</strong>
    {children}
    <div className="report-section-actions">
      <IconButton aria-label={`Move ${name} up`} disabled={index === 0} icon={ArrowUpIcon} size="small" onClick={() => onMove(-1)} />
      <IconButton aria-label={`Move ${name} down`} disabled={index === sectionCount - 1} icon={ArrowDownIcon} size="small" onClick={() => onMove(1)} />
      <Button leadingVisual={TrashIcon} size="small" tone="danger" onClick={onDelete}>Delete</Button>
    </div>
  </div>;
}

function ReportSectionHeading({ detail, id, index, label }) {
  return <div className="report-section-heading">
    <h2 id={id}><span>Section {index + 1}: </span><strong>{label}</strong></h2>
    {detail ? <Text>{detail}</Text> : null}
  </div>;
}

function AssetsSection({ actualProfile, editorProps, index, portfolioValue, rows }) {
  return <div className="report-section-block">
    <SectionEditor {...editorProps} />
    <section className="report-section" aria-labelledby={`report-section-${index + 1}`}>
      <ReportSectionHeading detail={portfolioValue !== null ? `${formatPortfolioAmount(portfolioValue)} total value` : "Add a portfolio value to calculate amounts."} id={`report-section-${index + 1}`} index={index} label="Assets" />
      {rows.length ? <div className="report-table-scroll"><table className="report-allocation-table">
        <thead><tr><th>Asset</th>{actualProfile ? <th className="numeric">Actual</th> : null}<th className="numeric">Target</th><th className="numeric">Target amount</th>{actualProfile ? <th className="numeric">Adjustment</th> : null}</tr></thead>
        <tbody>{rows.map((row) => <tr key={row.asset.id}>
          <th scope="row"><strong className={row.asset.name && row.asset.name !== row.asset.id ? undefined : "report-copy-value"}>{row.asset.name || row.asset.id}</strong>{row.asset.name && row.asset.name !== row.asset.id ? <Text className="report-copy-value">{row.asset.id}</Text> : null}</th>
          {actualProfile ? <td className="numeric">{formatPercentage(row.actualBasisPoints)}%</td> : null}
          <td className="numeric">{formatPercentage(row.targetBasisPoints)}%</td>
          <td className="numeric emphasized">{formatAmount(portfolioValue, row.targetBasisPoints)}</td>
          {actualProfile ? <td className={`numeric adjustment ${adjustmentTone(row.adjustmentBasisPoints)}`}>{formatAdjustment(portfolioValue, row.adjustmentBasisPoints)}</td> : null}
        </tr>)}</tbody>
      </table></div> : <Text as="p" className="report-empty">This target profile has no allocation recorded yet.</Text>}
    </section>
  </div>;
}

function PortfolioBreakdownSection({ actualProfile, editorProps, index, onViewChange, portfolioValue, targetProfile, viewId }) {
  const views = targetProfile.data.browserViews ?? [];
  const view = views.find((item) => item.id === viewId) ?? views[0] ?? null;
  const nodes = useMemo(() => view ? breakdownNodes(targetProfile.data, actualProfile?.data, view) : [], [actualProfile, targetProfile, view]);
  return <div className="report-section-block">
    <SectionEditor {...editorProps}>
      {views.length ? <FormControl>
        <FormControl.Label>Browser view</FormControl.Label>
        <Select block value={view?.id ?? ""} onChange={(event) => onViewChange(event.target.value)}>
          {views.map((item) => <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>)}
        </Select>
      </FormControl> : null}
      {!views.length ? <Text>No saved views in the Target profile.</Text> : null}
    </SectionEditor>
    <section className="report-section report-breakdown-section" aria-labelledby={`report-section-${index + 1}`}>
      <ReportSectionHeading detail={view?.name} id={`report-section-${index + 1}`} index={index} label="Portfolio breakdown" />
      {nodes.length ? <div className={`report-breakdown${actualProfile ? " compared" : ""}`}>
        <div className="breakdown-row heading"><span>Group or asset</span>{actualProfile ? <span className="numeric">Actual</span> : null}<span className="numeric">Target</span><span className="numeric">Target amount</span></div>
        <BreakdownRows actualProfile={actualProfile} nodes={nodes} portfolioValue={portfolioValue} targetDocument={targetProfile.data} view={view} />
      </div> : <Text as="p" className="report-empty">{view ? "This view has no allocated assets to display." : "Choose a Target profile with a saved Browser view."}</Text>}
    </section>
  </div>;
}

function BreakdownRows({ actualProfile, nodes, portfolioValue, targetDocument, view }) {
  return nodes.map((node) => node.asset
    ? <div className="breakdown-row asset" key={`asset:${node.asset.id}`}>
      <span className="breakdown-identity"><strong className={node.asset.name && node.asset.name !== node.asset.id ? undefined : "report-copy-value"}>{node.asset.name || node.asset.id}</strong>{node.asset.name && node.asset.name !== node.asset.id ? <small className="report-copy-value">{node.asset.id}</small> : null}<BreakdownDecorations asset={node.asset} document={targetDocument} view={view} /></span>
      {actualProfile ? <span className="numeric">{formatPercentage(node.actualBasisPoints)}%</span> : null}<span className="numeric">{formatPercentage(node.targetBasisPoints)}%</span><span className="numeric emphasized">{formatAmount(portfolioValue, node.targetBasisPoints)}</span>
    </div>
    : <div className="breakdown-group" key={`${node.propertyId}:${node.id}`}>
      <div className="breakdown-row group"><strong>{node.name}</strong>{actualProfile ? <span className="numeric">{formatPercentage(node.actualBasisPoints)}%</span> : null}<span className="numeric">{formatPercentage(node.targetBasisPoints)}%</span><span className="numeric emphasized">{formatAmount(portfolioValue, node.targetBasisPoints)}</span></div>
      <div className="breakdown-children"><BreakdownRows actualProfile={actualProfile} nodes={node.children} portfolioValue={portfolioValue} targetDocument={targetDocument} view={view} /></div>
    </div>);
}

function BreakdownDecorations({ asset, document, view }) {
  const values = (view.displayProperties ?? []).map((propertyId) => {
    const property = document.properties.find((item) => item.id === propertyId);
    return property ? `${property.name}: ${effectiveValue(document, asset.id, property.id).name}` : null;
  }).filter(Boolean);
  return values.length ? <small>{values.join(" · ")}</small> : null;
}

function PoliciesSection({ actualProfile, editorProps, index, targetProfile }) {
  const policies = useMemo(() => reportPolicies(targetProfile.data, actualProfile?.data), [actualProfile, targetProfile]);
  return <div className="report-section-block">
    <SectionEditor {...editorProps} />
    <section className="report-section report-policies" aria-labelledby={`report-section-${index + 1}`}>
      <ReportSectionHeading detail={`${policies.length} ${policies.length === 1 ? "policy" : "policies"}`} id={`report-section-${index + 1}`} index={index} label="Policies" />
      {policies.length ? <div className="report-policy-list">{policies.map(({ findings, policy, status }) => <article className="report-policy" key={policy.id}>
        <h3>{reportPolicyTitle(targetProfile.data, policy)}</h3>
        <p className="report-policy-definition">{policyDefinition(policy)}</p>
        <div className="report-policy-summary"><Text>Status: <strong className={`report-policy-status ${status}`}>{policyStatusLabel(status)}</strong></Text><PolicyRangeMeter policy={policy} /></div>
        {findings.length ? <div className="report-policy-findings">{["non-compliant", "warning"].map((findingStatus) => {
          const statusFindings = findings.filter((finding) => finding.result.status === findingStatus);
          return statusFindings.length ? <div className="finding-group" key={findingStatus}>
            <strong>{findingStatus === "non-compliant" ? "Violations" : "Advisories"}</strong>
            {statusFindings.map((finding) => <div className="finding" key={finding.result.key}>
              <span className="finding-subject">{findingSubject(targetProfile.data, policy, finding.result)}</span>
              <span>{findingDescription(policy, finding.result)}</span>
            </div>)}
          </div> : null;
        })}</div> : null}
      </article>)}</div> : <Text as="p" className="report-empty">This Target profile has no policies.</Text>}
    </section>
  </div>;
}

function reportPolicies(targetDocument, actualDocument) {
  const targetEvaluations = evaluateCompliance(targetDocument);
  const evaluationDocument = actualDocument ? { ...targetDocument, allocations: actualDocument.allocations } : targetDocument;
  const actualEvaluations = actualDocument ? evaluateCompliance(evaluationDocument) : targetEvaluations;
  const actualById = new Map(actualEvaluations.map((evaluation) => [evaluation.policy.id, evaluation]));
  return targetEvaluations.map((target) => {
    const actual = actualById.get(target.policy.id) ?? target;
    const targetByKey = new Map(target.results.map((result) => [result.key, result]));
    const relevantResults = actual.results.filter((result) => result.basisPoints > 0 || (targetByKey.get(result.key)?.basisPoints ?? 0) > 0);
    const findings = relevantResults.filter((result) => result.status === "non-compliant" || result.status === "warning")
      .map((result) => ({ result, gap: findingGap(target.policy, result) }))
      .sort((left, right) => findingRank(right.result.status) - findingRank(left.result.status) || right.gap - left.gap || left.result.label.localeCompare(right.result.label, undefined, { sensitivity: "base" }));
    return { policy: target.policy, findings, status: policyResultsStatus(relevantResults) };
  }).sort((left, right) => reportPolicyTitle(targetDocument, left.policy).localeCompare(reportPolicyTitle(targetDocument, right.policy), undefined, { sensitivity: "base" }));
}

function reportPolicyTitle(document, policy) {
  return policy.scope.type === "concentration" ? `Any ${policyTitle(document, policy)}` : policyTitle(document, policy);
}

function policyResultsStatus(results) {
  if (!results.length) return "not-evaluated";
  if (results.some((result) => result.status === "non-compliant")) return "non-compliant";
  if (results.some((result) => result.status === "warning")) return "warning";
  if (results.some((result) => result.status === "on-target")) return "on-target";
  return "compliant";
}

function findingRank(status) {
  return status === "non-compliant" ? 2 : status === "warning" ? 1 : 0;
}

function findingGap(policy, result) {
  return distanceOutsideRange(result.basisPoints, result.status === "non-compliant" ? policy.must : policy.should);
}

function distanceOutsideRange(value, bounds) {
  return Math.max((bounds.minimum ?? 0) - value, value - (bounds.maximum ?? 10000), 0);
}

function policyStatusLabel(status) {
  return { "non-compliant": "Violation", warning: "Advisory", "on-target": "Compliant", compliant: "Compliant", "not-evaluated": "Not applicable" }[status] ?? status;
}

function policyDefinition(policy) {
  const clauses = [];
  if (policy.must.minimum !== undefined) clauses.push(`MUST be at least ${formatPercentage(policy.must.minimum)}%`);
  clauses.push(`MUST not exceed ${formatPercentage(policy.must.maximum)}%`);
  if (policy.should?.minimum !== undefined) clauses.push(`SHOULD be at least ${formatPercentage(policy.should.minimum)}%`);
  if (policy.should?.maximum !== undefined) clauses.push(`SHOULD not exceed ${formatPercentage(policy.should.maximum)}%`);
  return clauses.join(" · ");
}

function findingSubject(document, policy, result) {
  if (policy.scope.type === "concentration" && policy.scope.dimension.type === "asset") {
    const asset = document.assets.find((item) => item.id === result.key);
    return <>{asset?.name || result.label}{asset?.name && asset.name !== asset.id ? <small className="report-copy-value">{asset.id}</small> : null}</>;
  }
  return result.label.replace(": ", " — ");
}

function findingDescription(policy, result) {
  const bounds = result.status === "non-compliant" ? policy.must : policy.should;
  const value = `${formatPercentage(result.basisPoints)}%`;
  if (bounds.minimum !== undefined && result.basisPoints < bounds.minimum) return `${value} is below ${formatPercentage(bounds.minimum)}%`;
  return `${value} exceeds ${formatPercentage(bounds.maximum)}%`;
}

function InlineReportText({ emptyLabel, label, maxLength, onChange, value }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const startingValue = useRef(value);
  const inputRef = useRef(null);
  const buttonRef = useRef(null);

  function beginEditing() {
    startingValue.current = value;
    setDraft(value);
    setEditing(true);
  }

  function closeEditor(restoreFocus) {
    setEditing(false);
    if (restoreFocus) requestAnimationFrame(() => buttonRef.current?.focus({ preventScroll: true }));
  }

  function changeDraft(nextDraft) {
    setDraft(nextDraft);
    onChange(nextDraft);
  }

  function cancel() {
    onChange(startingValue.current);
    setDraft(startingValue.current);
    closeEditor(true);
  }

  return <span className="report-editable-copy">
    <span className="report-output" hidden>{value}</span>
    {editing
      ? <input ref={inputRef} className="report-inline-input report-editor-only" aria-label={`Edit ${label}`} autoFocus maxLength={maxLength} size={Math.max(12, Math.min(36, draft.length + 1))} value={draft} onBlur={() => closeEditor(false)} onChange={(event) => changeDraft(event.target.value)} onFocus={(event) => event.currentTarget.select()} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); closeEditor(true); } if (event.key === "Escape") { event.preventDefault(); cancel(); } }} />
      : <button ref={buttonRef} className={`report-editable report-editor-only${value ? "" : " empty"}`} type="button" aria-label={`Edit ${label}`} onClick={beginEditing}>{value || emptyLabel}</button>}
  </span>;
}

function ReportMetadata({ actualProfile, targetProfile }) {
  return <footer className="report-metadata">
    <strong>Regulator sources</strong>
    <dl><div><dt>Target profile</dt><dd>{targetProfile.name}</dd></div>{actualProfile ? <div><dt>Actual profile</dt><dd>{actualProfile.name}</dd></div> : null}</dl>
  </footer>;
}

function allocationRows(targetProfile, actualProfile) {
  const actualAllocations = new Map((actualProfile?.data.allocations ?? []).map((allocation) => [allocation.assetId, allocation.basisPoints]));
  const targetAllocations = new Map((targetProfile.data.allocations ?? []).map((allocation) => [allocation.assetId, allocation.basisPoints]));
  return targetProfile.data.assets.map((asset) => {
    const targetBasisPoints = targetAllocations.get(asset.id) ?? 0;
    const actualBasisPoints = actualAllocations.get(asset.id) ?? 0;
    return { asset, actualBasisPoints, targetBasisPoints, adjustmentBasisPoints: targetBasisPoints - actualBasisPoints };
  }).filter((row) => row.targetBasisPoints > 0 || row.actualBasisPoints > 0)
    .sort((left, right) => right.targetBasisPoints - left.targetBasisPoints || (left.asset.name || left.asset.id).localeCompare(right.asset.name || right.asset.id));
}

function breakdownNodes(targetDocument, actualDocument, view) {
  const assets = targetDocument.assets.filter((asset) => (
    matchesViewFilter(targetDocument, asset, view.filter)
    && (assetAllocation(targetDocument, asset.id) > 0 || assetAllocation(actualDocument, asset.id) > 0)
  ));
  return breakdownLevel(targetDocument, actualDocument, assets, view, 0);
}

function breakdownLevel(targetDocument, actualDocument, assets, view, depth) {
  const property = targetDocument.properties.find((item) => item.id === view.groupBy[depth]);
  if (!property) return sortBreakdownAssets(targetDocument, assets, view).map((asset) => ({
    asset,
    actualBasisPoints: assetAllocation(actualDocument, asset.id),
    targetBasisPoints: assetAllocation(targetDocument, asset.id)
  }));
  const groups = property.values.map((value) => {
    const groupAssets = assets.filter((asset) => effectiveValue(targetDocument, asset.id, property.id).id === value.id);
    return {
      id: value.id,
      propertyId: property.id,
      name: value.name,
      assets: groupAssets,
      actualBasisPoints: allocationTotal(actualDocument, groupAssets),
      targetBasisPoints: allocationTotal(targetDocument, groupAssets)
    };
  }).filter((group) => group.assets.length);
  return sortBreakdownGroups(groups, view.sort).map((group) => ({
    ...group,
    children: breakdownLevel(targetDocument, actualDocument, group.assets, view, depth + 1)
  }));
}

function sortBreakdownGroups(groups, sort) {
  if (sort === "allocation-desc") return [...groups].sort((left, right) => right.targetBasisPoints - left.targetBasisPoints);
  if (sort === "allocation-asc") return [...groups].sort((left, right) => left.targetBasisPoints - right.targetBasisPoints);
  if (sort === "name") return [...groups].sort((left, right) => left.name.localeCompare(right.name));
  return groups;
}

function sortBreakdownAssets(document, assets, view) {
  const sorted = [...assets];
  if (view.sort === "allocation-desc") return sorted.sort((left, right) => assetAllocation(document, right.id) - assetAllocation(document, left.id));
  if (view.sort === "allocation-asc") return sorted.sort((left, right) => assetAllocation(document, left.id) - assetAllocation(document, right.id));
  if (view.sort === "name") return sorted.sort(compareAssetNames);
  if (view.sort === "decoration") {
    const property = document.properties.find((item) => item.id === view.displayProperties?.[0]);
    if (property) return sorted.sort((left, right) => propertyValueIndex(document, property, left.id) - propertyValueIndex(document, property, right.id) || compareAssetNames(left, right));
  }
  return sorted.sort((left, right) => document.assets.indexOf(left) - document.assets.indexOf(right));
}

function matchesViewFilter(document, asset, filter) {
  return !filter || effectiveValue(document, asset.id, filter.propertyId).id === filter.valueId;
}

function propertyValueIndex(document, property, assetId) {
  return property.values.findIndex((value) => value.id === effectiveValue(document, assetId, property.id).id);
}

function compareAssetNames(left, right) {
  return (left.name || left.id).localeCompare(right.name || right.id, undefined, { sensitivity: "base" }) || left.id.localeCompare(right.id, undefined, { sensitivity: "base" });
}

function assetAllocation(document, assetId) {
  return document?.allocations?.find((allocation) => allocation.assetId === assetId)?.basisPoints ?? 0;
}

function allocationTotal(document, assets) {
  return assets.reduce((total, asset) => total + assetAllocation(document, asset.id), 0);
}

function profilesAreComparable(left, right) {
  const leftIds = left.data.assets.map((asset) => asset.id).sort();
  const rightIds = right.data.assets.map((asset) => asset.id).sort();
  return leftIds.length === rightIds.length && leftIds.every((id, index) => id === rightIds[index]);
}

function formatAmount(portfolioValue, basisPoints) {
  return portfolioValue === null ? "—" : formatPortfolioAmount(amountForBasisPoints(portfolioValue, basisPoints));
}

function formatAdjustment(portfolioValue, basisPoints) {
  if (portfolioValue === null) return "—";
  if (basisPoints === 0) return "No change";
  const verb = basisPoints > 0 ? "Buy" : "Sell";
  return `${verb} ${formatPortfolioAmount(Math.abs(amountForBasisPoints(portfolioValue, basisPoints)))}`;
}

function adjustmentTone(basisPoints) {
  if (basisPoints > 0) return "buy";
  if (basisPoints < 0) return "sell";
  return "unchanged";
}

function parsePortfolioValue(input) {
  const normalized = input.replaceAll(",", "").trim();
  if (!/^\d+(?:\.\d{0,2})?$/.test(normalized)) return null;
  const value = Number(normalized);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function caretForLogicalOffset(input, logicalOffset) {
  if (logicalOffset === 0) return 0;
  let logicalCharacters = 0;
  for (let index = 0; index < input.length; index += 1) {
    if (input[index] !== ",") logicalCharacters += 1;
    if (logicalCharacters === logicalOffset) return index + 1;
  }
  return input.length;
}

function validReportDraft(value) {
  if (!value || typeof value !== "object") return null;
  const sectionOrder = validSectionOrder(value.sectionOrder) ?? [
    ...(value.allocationIncluded === false ? [] : ["assets"]),
    ...(value.breakdownIncluded === true ? ["breakdown"] : [])
  ];
  return {
    targetProfileId: validDraftString(value.targetProfileId, 200, ""),
    actualProfileId: validDraftString(value.actualProfileId, 200, ""),
    portfolioValueInput: validDraftString(value.portfolioValueInput, 64, ""),
    reportTitle: validDraftString(value.reportTitle, 80, defaultReportDraft.reportTitle),
    recipientName: validDraftString(value.recipientName, 120, ""),
    sectionOrder,
    breakdownViewId: validDraftString(value.breakdownViewId, 200, "")
  };
}

function validSectionOrder(value) {
  if (!Array.isArray(value)) return null;
  const validIds = new Set(reportSections.map((section) => section.id));
  return value.filter((id, index) => validIds.has(id) && value.indexOf(id) === index);
}

function validDraftString(value, maximumLength, fallback) {
  return typeof value === "string" && value.length <= maximumLength ? value : fallback;
}

export function standaloneReportHtml(title, reportMarkup) {
  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(title)}</title>
  <style>
${reportDocumentStyles}
${policyRangeMeterStyles}
${reportExportStyles}
  </style>
</head>
<body class="report-export">
${reportMarkup}
</body>
</html>`;
}

export function reportMarkupForDownload(reportElement) {
  const report = reportElement.cloneNode(true);
  report.querySelectorAll(".report-editor-only").forEach((element) => element.remove());
  report.querySelectorAll(".report-output").forEach((element) => element.removeAttribute("hidden"));
  return report.outerHTML;
}

const reportExportStyles = `
@page {
  size: A4 portrait;
  margin: 16mm;
}

* { box-sizing: border-box; }
html { background: #f6f8fa; }
body.report-export {
  min-height: 100vh;
  margin: 0;
  padding: 24px;
  background: #f6f8fa;
}

@media print {
  html, body.report-export { background: #fff; }
  body.report-export { min-height: 0; padding: 0; }
  .report-document {
    width: auto;
    min-height: 0;
    margin: 0;
    padding: 0;
    border: 0;
    box-shadow: none;
  }
  .report-allocation-table tr { break-inside: avoid; }
}`;

function downloadHtmlFile(filename, contents) {
  const url = URL.createObjectURL(new Blob([contents], { type: "text/html;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function reportFilename(profileName) {
  const safeName = profileName.trim().replace(/[<>:"/\\|?*]/g, "-").split("").map((character) => character.charCodeAt(0) < 32 ? "-" : character).join("") || "portfolio";
  return `${safeName} - report.html`;
}

function escapeHtml(value) {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}

function ReportBuilderLoading() {
  return <main className="report-builder-page" aria-busy="true"><header className="report-builder-header"><Button as="a" href="#/profiles" tone="secondary" leadingVisual={ArrowLeftIcon}>Back to Profiles</Button><div><h1>Report builder</h1><Text as="p">Loading profiles…</Text></div></header></main>;
}
